import { useFormContext, useFormState } from 'react-hook-form';
import { selectUserProject } from 'core/userContext';
import { useCloneJourneyCrossProject } from 'modules/workflows/components/modals/CloneJourneyModal/hooks';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { BUTTON_SIZES, PrimaryButton } from 'ui/button';
import { MODAL_SIZES, MODAL_VARIANTS, Modal, ModalBody, ModalFooter, ModalHeader } from 'ui/modal';
import { spacing } from 'ui/theme';

import { CloneJourney } from 'modules/workflows/components/modals/CloneJourneyModal/CloneJourney';
import {
  CloneJourneyFormProvider,
  CloningCampaignsContextProvider,
  useCloningCampaignsContext,
} from 'modules/workflows/components/modals/CloneJourneyModal/context';
import { Workflow, WorkflowType } from '../types';

const displayText = {
  modalTitle: 'Copy journey',
  cancelButton: 'Cancel',
  confirmButton: 'Copy journey',
};

const ConfirmUnarchiveButtonWithSpacing = styled(PrimaryButton)`
  svg {
    margin-right: ${spacing.quarter};
  }
`;

const StyledContainer = styled.div`
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  > :not(:last-child) {
    margin-bottom: 1rem;
  }
`;

type CopyWorkflowDialogProps = {
  onClose: () => void;
  onConfirm: (workflow: Workflow, name: string) => void;
  workflow: Workflow;
  handleSwitchToProjectJourney?: (workflowId: number, projectId: number) => void;
};

const CopyWorkflowDialogBody = ({
  onClose,
  onConfirm,
  workflow,
  handleSwitchToProjectJourney,
}: CopyWorkflowDialogProps) => {
  const currentProject = useSelector(selectUserProject);
  const { selectedProject, wfNameInput, copyWebhooks, copyCampaigns, containerRef } =
    useCloningCampaignsContext();
  const isDifferentProject = selectedProject !== currentProject?.id;
  const isCrossProject = selectedProject && isDifferentProject;
  const { getValues } = useFormContext();
  const { isValid } = useFormState();

  const closeCloneJourneyModal = () => {
    // don't close modal if user is editing text
    if (!window?.getSelection()?.toString()) onClose();
  };

  const { handleCloneJourney } = useCloneJourneyCrossProject({
    workflowName: wfNameInput,
    handleSwitchToProjectJourney,
  });

  const handleConfirm = () => {
    const campaignValues = getValues();

    if (isCrossProject) {
      // type mismatches forced me to pass a trimmed workflow object with a defined workflowType
      handleCloneJourney(
        { workflowType: workflow.workflowType as WorkflowType, id: workflow.id, name: wfNameInput },
        selectedProject,
        copyWebhooks, // won't work for now, slated for future work
        copyCampaigns ? campaignValues : {},
      );
      onClose();
    } else {
      onConfirm(workflow, wfNameInput);
    }
  };

  return (
    <Modal
      data-test="copy-workflow-modal-wrapper"
      id="copy-workflow-modal"
      isModalOpen={true}
      onClose={closeCloneJourneyModal}
      size={MODAL_SIZES.LARGE}
      variant={MODAL_VARIANTS.DEFAULT}
    >
      <ModalHeader data-test="copy-workflow-modal-header">{displayText.modalTitle}</ModalHeader>
      <ModalBody data-test="copy-workflow-modal-body" id="copy-workflow-modal-body">
        <StyledContainer ref={containerRef}>
          <CloneJourney />
        </StyledContainer>
      </ModalBody>
      <ModalFooter
        cancelLabel={displayText.cancelButton}
        data-test="copy-workflow-modal-actions"
        id="copy-workflow-modal-actions"
      >
        <ConfirmUnarchiveButtonWithSpacing
          onClick={handleConfirm}
          size={BUTTON_SIZES.LARGE}
          isDisabled={!!isCrossProject && copyCampaigns && !isValid}
        >
          {displayText.confirmButton}
        </ConfirmUnarchiveButtonWithSpacing>
      </ModalFooter>
    </Modal>
  );
};

export const CopyWorkflowDialog = (props: CopyWorkflowDialogProps) => {
  return (
    <CloneJourneyFormProvider>
      <CloningCampaignsContextProvider selectedWorkflow={props?.workflow}>
        <CopyWorkflowDialogBody {...props} />
      </CloningCampaignsContextProvider>
    </CloneJourneyFormProvider>
  );
};
